import { Link, Typography } from '@mui/material'
import { z } from 'zod'

import { isPhone } from '@/app/component/form/utils/validation'
import { MIN_PASSWORD_LENGTH } from '@/app/service/validate'
import { SelectOption } from '@/app/types'

export type ProfileFormValues = {
  firstName: string
  lastName: string
  country: string
  organization: string
  organizationType: string
  hearAboutUs: string
}

export type AccountFormValues = {
  email: string
  phone: string
  password: string
  terms: boolean
}

export type SignupValues = ProfileFormValues &
  AccountFormValues & {
    verifyToken: string
    emailToken: string
  }

export type InvitationProfileFormValues = {
  firstName: string
  lastName: string
  country: string
}

export type InvitationValues = InvitationProfileFormValues & AccountFormValues

const baseProfileSchema = z.object({
  firstName: z.string({ message: 'First name is required' }).min(1),
  lastName: z.string({ message: 'Last name is required' }).min(1),
  country: z.string({ message: 'Country is required' }).min(1),
})

export const signupProfileSchema = baseProfileSchema.extend({
  organization: z.string({ message: 'Organization name is required' }).min(1),
  organizationType: z.string({ message: 'Organization type is required' }).min(1),
  hearAboutUs: z.string().optional(),
})

export const invitationProfileSchema = baseProfileSchema

export const accountSchema = z.object({
  email: z.string({ message: 'Email is required' }).min(1).email({ message: 'Invalid email address' }),
  phone: z.string().describe('Phone number').transform(isPhone(true)),
  password: z
    .string({
      message: 'Password is required',
    })
    .min(1)
    .min(MIN_PASSWORD_LENGTH, { message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.` }),
  terms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Terms of Service to create an account.',
  }),
})

export const getProfileDefaultValues = (
  defaultCountry: string,
  organizationOptions: SelectOption<string>[],
): ProfileFormValues => ({
  firstName: '',
  lastName: '',
  organization: '',
  organizationType: organizationOptions[0]?.value || '',
  country: defaultCountry,
  hearAboutUs: '',
})

export const getProfileFields = (countryOptions: SelectOption<string>[], organizationOptions: SelectOption<string>[]) =>
  ({
    firstName: {
      fieldProps: { label: 'First Name', name: 'firstName', type: 'text', required: true },
      type: 'text',
    },
    lastName: {
      fieldProps: { label: 'Last Name', name: 'lastName', type: 'text', required: true },
      type: 'text',
    },
    organization: {
      fieldProps: { label: 'Organization name', name: 'organization', type: 'text', required: true },
      type: 'text',
    },
    organizationType: {
      fieldProps: {
        label: 'Organization Type',
        name: 'organizationType',
        type: 'select',
        required: true,
        options: organizationOptions,
      },
      type: 'select',
    },
    country: {
      fieldProps: {
        label: 'Country',
        name: 'country',
        type: 'select',
        required: true,
        options: countryOptions,
      },
      type: 'autocomplete',
    },
    hearAboutUs: {
      fieldProps: {
        label: 'How did you hear about us?',
        name: 'hearAboutUs',
        options: [
          { label: 'Google (or other search engine)', value: 'Google (or other search engine)' },
          { label: 'ChatGPT (or other AI)', value: 'ChatGPT (or other AI)' },
          { label: 'LinkedIn', value: 'LinkedIn' },
          { label: 'Referral from customer / partner', value: 'Referral from customer / partner' },
          { label: 'Webinar / online event', value: 'Webinar / online event' },
          { label: 'Email from engageSPARK', value: 'Email from engageSPARK' },
          { label: 'Call / demo with engageSPARK', value: 'Call / demo with engageSPARK' },
          { label: 'Conference', value: 'Conference' },
          { label: 'News / article / blog', value: 'News / article / blog' },
        ],
        otherValue: 'Other',
        otherTextLabel: 'Other (please specify)',
      },
      type: 'select-other',
    },
  }) as const

export const getAccountFields = (defaultCountryCode: string) =>
  ({
    email: {
      fieldProps: {
        label: 'Email',
        name: 'email',
        type: 'email',
        required: true,
        disabled: true, // email comes from the email params and should not be editable
      },
      type: 'text',
    },
    phone: {
      fieldProps: {
        label: 'Phone number',
        name: 'phone',
        type: 'tel',
        required: true,
        defaultCountry: defaultCountryCode,
      },
      type: 'tel',
    },
    password: {
      fieldProps: {
        info: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
        label: 'Password',
        name: 'password',
        type: 'password',
        required: true,
      },
      type: 'password',
    },
    terms: {
      fieldProps: {
        name: 'terms',
        label: (
          <Typography color="textPrimary">
            <Typography component="span">I read and accept the </Typography>
            <Link href="/terms-of-service" target="_blank" color="primary">
              Terms of Service
            </Link>
          </Typography>
        ),
      },
      type: 'single-checkbox',
    },
  }) as const

export const getInvitationProfileDefaultValues = (defaultCountry: string): InvitationProfileFormValues => ({
  firstName: '',
  lastName: '',
  country: defaultCountry,
})

export const getInvitationProfileFields = (countryOptions: SelectOption<string>[]) =>
  ({
    firstName: {
      fieldProps: { label: 'First Name', name: 'firstName', type: 'text', required: true },
      type: 'text',
    },
    lastName: {
      fieldProps: { label: 'Last Name', name: 'lastName', type: 'text', required: true },
      type: 'text',
    },
    country: {
      fieldProps: {
        label: 'Country',
        name: 'country',
        type: 'select',
        required: true,
        options: countryOptions,
      },
      type: 'autocomplete',
    },
  }) as const
